import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { ChevronDown, Plus, Check } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Pre-defined exact colors matching the design spec
const COLORS = {
  current: "#2dd4bf", // Teal
  prior: "#3b82f6", // Blue
  prior2: "#60a5fa", // Light Blue
  prior3: "#818cf8", // Indigo
  prior4: "#a78bfa", // Violet
  trailing: "#f59e0b", // Orange/Yellow
  record: "#ef4444", // Red
  yoy1: "#8b5cf6", // Purple
  yoy2: "#c084fc",
  custom: ["#a3e635", "#30c5d8", "#ffb64d", "#ff6f69"],
};

export default function WeeklyTrendChart({
  weeks = [],
  height = 360,
  isWheelZoomActive = false,
  onAddCustomWeek,
}) {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const { theme, colors: themeColors } = useTheme();

  // Selected series management
  const [activeSeriesKeys, setActiveSeriesKeys] = useState({});
  const [isAddingCustomWeek, setIsAddingCustomWeek] = useState(false);
  const [customDate, setCustomDate] = useState("");

  // Calculate the max allowable date to "block" the 5 recent weeks in the calendar
  // const maxAllowedDate = useMemo(() => {
  //   const d = new Date();
  //   d.setDate(d.getDate() - 35); // 5 weeks ago
  //   return d.toISOString().split("T")[0];
  // }, []);
  const maxAllowedDate = useMemo(() => {
    const d = new Date(); // June 5, 2026
    
    // 1. Move to the most recent Sunday (May 31)
    const day = d.getDay(); 
    const diff = d.getDate() - day; 
    const currentWeekSunday = new Date(d.setDate(diff));

    // 2. Subtract 28 days (4 weeks) to get to the Sunday of the 4th week back (May 3rd)
    // 35 days would get us to the Sunday 5 weeks ago. 
    // If you want May 3rd, we go back 4 weeks from the current week's Sunday.
    currentWeekSunday.setDate(currentWeekSunday.getDate() - 28);
    
    return currentWeekSunday.toISOString().split("T")[0];
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsAddingCustomWeek(false);
      }
    };
    document.addEventListener("click", handleClickOutside, { capture: true });
    return () => {
      document.removeEventListener("click", handleClickOutside, { capture: true });
    };
  }, []);

  // 1. Process and Standardize API Data into structured categories matching the image
  const processedSeries = useMemo(() => {
    const categories = {
      recent: [],
      baselines: [],
      yoy: [],
      custom: [],
    };

    let customColorIndex = 0;

    weeks.forEach((w, index) => {
      const lowerName = String(w.name || "").toLowerCase();
      const lowerPlace = String(w.week_place || "").toLowerCase();
      
      const identifier = lowerPlace || lowerName;

      let primaryLabel = w.week_place || w.name;
      let secondaryLabel = w.date_range || w.name;

      let type = "custom";
      let color = COLORS.custom[customColorIndex % COLORS.custom.length];
      let dashArray = 0;
      let strokeWidth = 3;
      let isLineIcon = false; 

      const isRecentFallback = !w.isCustom && index < 5 && !identifier.includes("trailing") && !identifier.includes("record") && !identifier.includes("same week") && !identifier.includes("ago") && !identifier.includes("last year");

      if (
        identifier.includes("current") ||
        identifier.includes("prior") ||
        isRecentFallback
      ) {
        type = "recent";
        
        if (identifier.includes("current")) {
            primaryLabel = "Current week";
            color = COLORS.current;
        } else if (identifier.includes("4 week")) {
            primaryLabel = "4 weeks prior";
            color = COLORS.prior4;
        } else if (identifier.includes("3 week")) {
            primaryLabel = "3 weeks prior";
            color = COLORS.prior3;
        } else if (identifier.includes("2 week")) {
            primaryLabel = "2 weeks prior";
            color = COLORS.prior2;
        } else if (identifier.includes("prior")) {
            primaryLabel = "Prior week";
            color = COLORS.prior;
        } else {
            if (index === 0) {
                primaryLabel = "Current week";
                color = COLORS.current;
            } else if (index === 1) {
                primaryLabel = "Prior week";
                color = COLORS.prior;
            } else if (index === 2) {
                primaryLabel = "2 weeks prior";
                color = COLORS.prior2;
            } else if (index === 3) {
                primaryLabel = "3 weeks prior";
                color = COLORS.prior3;
            } else {
                primaryLabel = "4 weeks prior";
                color = COLORS.prior4;
            }
        }
      } else if (identifier.includes("trailing") || identifier.includes("baseline")) {
        type = "baselines";
        primaryLabel = "Trailing 4-week avg";
        secondaryLabel = ""; // Force clear so it doesn't show duplicate text on the right side
        color = COLORS.trailing;
        dashArray = 5;
        strokeWidth = 2;
        isLineIcon = true;
      } else if (identifier.includes("record") || identifier.includes("peak") || identifier.includes("max")) {
        type = "baselines";
        primaryLabel = "Record high";
        secondaryLabel = w.date_range || ""; // Do not fallback to w.name to avoid duplicate text
        color = COLORS.record;
        dashArray = 0;
        strokeWidth = 2;
        isLineIcon = true;
      } else if (identifier.includes("same week") || identifier.includes("last year") || identifier.includes("ago")) {
        type = "yoy";
        
        if (lowerPlace.includes("same week") || lowerPlace.includes("year") || lowerPlace.includes("ago")) {
           primaryLabel = w.week_place;
           secondaryLabel = w.date_range || w.name; 
        } else {
           primaryLabel = w.name;
           secondaryLabel = w.week_place || w.date_range;
        }

        color = identifier.includes("2024") || identifier.includes("2 years") ? COLORS.yoy2 : COLORS.yoy1;
      } else {
        type = "custom";
        primaryLabel = "Custom";
        secondaryLabel = w.date_range || w.name; 
        customColorIndex++;
      }

      // Case-insensitive check to prevent duplicate labels on the right side
      if (String(primaryLabel).toLowerCase() === String(secondaryLabel).toLowerCase()) {
        secondaryLabel = "";
      }

      categories[type].push({
        id: w.name + index, 
        originalName: w.name,
        primaryLabel,
        secondaryLabel,
        data: w.data,
        color,
        dashArray,
        strokeWidth,
        isLineIcon,
        type,
      });
    });

    // Explicitly sort "recent" category from Current -> 4 weeks prior
    categories.recent.sort((a, b) => {
      const order = [
        "current week",
        "prior week",
        "2 weeks prior",
        "3 weeks prior",
        "4 weeks prior"
      ];
      const getIndex = (label) => {
        const lowerLabel = String(label).toLowerCase();
        const foundIndex = order.findIndex(o => lowerLabel.includes(o));
        return foundIndex === -1 ? 999 : foundIndex;
      };
      return getIndex(a.primaryLabel) - getIndex(b.primaryLabel);
    });

    return categories;
  }, [weeks]);

  // 2. Initialize Default Selections on first load
  useEffect(() => {
    if (weeks.length > 0 && Object.keys(activeSeriesKeys).length === 0) {
      const initial = {};
      Object.values(processedSeries).flat().forEach((s) => {
        const lowerLabel = s.primaryLabel.toLowerCase();
        if (
          lowerLabel.includes("current") ||
          lowerLabel.includes("prior week") ||
          lowerLabel.includes("trailing") ||
          s.type === "custom" 
        ) {
          initial[s.id] = true;
        } else {
          initial[s.id] = false;
        }
      });
      setActiveSeriesKeys(initial);
    }
  }, [processedSeries, weeks.length]); 

  // 3. Flatten structured data into ApexCharts series format based on active toggles
  const activeSeries = useMemo(() => {
    return Object.values(processedSeries)
      .flat()
      .filter((s) => activeSeriesKeys[s.id])
      .map((s) => ({
        name: s.id,
        primaryLabel: s.primaryLabel,
        secondaryLabel: s.secondaryLabel,
        data: s.data,
        color: s.color,
        dashArray: s.dashArray,
        strokeWidth: s.strokeWidth,
        isLineIcon: s.isLineIcon,
      }));
  }, [processedSeries, activeSeriesKeys]);

  const toggleSeries = (id) => {
    setActiveSeriesKeys((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // 4. ApexCharts Configuration
  const options = useMemo(() => {
    const axisTextColor = themeColors.textSecondary || themeColors.text;
    const gridColor = themeColors.border || (theme === "white" ? "#d8e2ef" : "#233d5e");
    const isLight = theme === "white" || theme === "light";

    return {
      chart: {
        toolbar: { show: true, tools: { download: false } },
        zoom: { enabled: true, type: "x", autoScaleYaxis: true, allowMouseWheelZoom: isWheelZoomActive },
        foreColor: axisTextColor,
        fontFamily: "inherit",
      },
      stroke: {
        curve: "smooth",
        width: activeSeries.map((s) => s.strokeWidth),
        dashArray: activeSeries.map((s) => s.dashArray),
      },
      colors: activeSeries.map((s) => s.color),
      xaxis: {
        categories: DAYS.map(d => d.slice(0,3)), 
        tooltip: { enabled: false },
        labels: {
          style: { colors: DAYS.map(() => axisTextColor), fontSize: "13px", fontWeight: 600 },
        },
        axisBorder: { show: true, color: gridColor },
        axisTicks: { show: true, color: gridColor },
      },
      yaxis: {
        labels: {
          formatter: (v) => {
            const val = Number(v || 0);
            return val >= 1000 ? `${Math.round(val / 1000).toLocaleString("en-US")}K` : val.toLocaleString("en-US");
          },
          style: { colors: [axisTextColor], fontSize: "12px", fontWeight: 600 },
        },
      },
      legend: { show: false }, 
      grid: {
        borderColor: gridColor,
        strokeDashArray: 3,
        padding: { bottom: 15 },
      },
      tooltip: {
        theme: "dark",
        shared: true,
        intersect: false,
        custom: function ({ series, dataPointIndex, w }) {
          const dayName = DAYS[dataPointIndex]; 
          
          const tooltipBg = isLight ? "#ffffff" : "#0f172a";
          const textColor = isLight ? "#0f172a" : "#f8fafc";
          const mutedColor = isLight ? "#64748b" : "#94a3b8";
          const borderColor = isLight ? "#e2e8f0" : "#334155";
          
          let trailingAvgVal = null;
          activeSeries.forEach((s, idx) => {
            if (s.primaryLabel.toLowerCase().includes("trailing")) {
              trailingAvgVal = series[idx][dataPointIndex];
            }
          });

          const rows = activeSeries
            .map((s, index) => {
              const val = series[index][dataPointIndex];
              if (val == null || isNaN(val)) return "";

              let pctHtml = `<div style="width: 45px; text-align: right;"></div>`; 
              const isBaseline = s.primaryLabel.toLowerCase().includes("trailing") || s.primaryLabel.toLowerCase().includes("record");
              
              if (trailingAvgVal && val !== trailingAvgVal && !isBaseline && trailingAvgVal > 0) {
                const pct = ((val - trailingAvgVal) / trailingAvgVal) * 100;
                const pctColor = pct > 0 ? "#10b981" : "#ef4444"; 
                const sign = pct > 0 ? "+" : "";
                pctHtml = `<div style="color: ${pctColor}; font-weight: 700; width: 45px; text-align: right;">${sign}${pct.toFixed(0)}%</div>`;
              }

              const iconHtml = s.isLineIcon 
                ? `<div style="width: 14px; height: 3px; background: ${s.color}; border-style: ${s.dashArray > 0 ? 'dashed' : 'solid'}; border-width: 0; border-top-width: 2px; border-color: ${s.color};"></div>`
                : `<div style="width: 8px; height: 8px; border-radius: 50%; background: ${s.color};"></div>`;

              return `
                <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 10px; font-size: 13px; color: ${textColor};">
                  <div style="display: flex; align-items: center; gap: 10px; width: 140px;">
                    ${iconHtml}
                    <span style="font-weight: 500;">${s.primaryLabel}</span>
                  </div>
                  <div style="display: flex; align-items: center; gap: 16px;">
                    <div style="font-weight: 700; width: 70px; text-align: right;">
                      ${Number(val).toLocaleString("en-US")}
                    </div>
                    ${pctHtml}
                  </div>
                </div>
              `;
            })
            .join("");

          return `
            <div style="background: ${tooltipBg}; border: 1px solid ${borderColor}; border-radius: 6px; padding: 14px 16px; min-width: 320px; box-shadow: 0 10px 25px rgba(0,0,0,0.4); font-family: inherit;">
              <div style="display: flex; justify-content: space-between; font-size: 13px; font-weight: 700; color: ${textColor}; margin-bottom: 12px;">
                <span>${dayName}</span>
                <span style="color: ${mutedColor}; font-weight: 500;">vs. trailing avg</span>
              </div>
              <div style="border-bottom: 1px solid ${borderColor}; margin-bottom: 6px;"></div>
              ${rows || `<div style="color: ${mutedColor}; font-size: 12px; margin-top: 10px;">No data</div>`}
            </div>
          `;
        },
      },
    };
  }, [activeSeries, theme, themeColors, isWheelZoomActive]);

  const renderDropdownSection = (title, items) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mb-4 last:mb-0">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2 px-2">
          {title}
        </div>
        {items.map((item) => {
          const isActive = activeSeriesKeys[item.id];
          
          return (
            <div
              key={item.id}
              onClick={(e) => { e.stopPropagation(); toggleSeries(item.id); }}
              className="flex items-center justify-between px-2 py-2 rounded-md hover:bg-slate-700/50 cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-4 flex justify-center">
                  {isActive ? (
                     item.isLineIcon ? (
                        <div style={{ width: '14px', borderTop: `2px ${item.dashArray ? 'dashed' : 'solid'} ${item.color}` }} />
                     ) : (
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                     )
                  ) : (
                     <div className="w-2.5 h-2.5 rounded-full border-[1.5px] border-slate-500" />
                  )}
                </div>
                
                <span className={`text-[13px] font-medium transition-colors ${isActive ? 'text-slate-100' : 'text-slate-400'}`}>
                  {item.primaryLabel}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                {item.secondaryLabel && (
                  <span className={`text-[12px] ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
                    {item.secondaryLabel}
                  </span>
                )}
                <div className="w-4">
                  {isActive && <Check size={16} className="text-blue-400" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col">
      {/* <div className="flex justify-end mb-2 w-full relative" ref={dropdownRef}>
        <div
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer transition-colors min-w-[200px]"
          style={{ backgroundColor: themeColors.cardBg, borderColor: themeColors.border }}
        >
          <span className="text-sm font-semibold" style={{ color: themeColors.text }}>Select Weeks</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: themeColors.textSecondary }} />
        </div> */}
       {/* <div className="w-full flex flex-col"> */}
       <div className="flex justify-end mb-2 w-full relative" ref={dropdownRef}>
        <div
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer transition-colors w-full min-w-[200px]"
          style={{ 
            backgroundColor: themeColors.cardBg, 
            borderColor: themeColors.border,
            minHeight: "42px" 
          }}
        >
          <div className="flex-1 flex flex-wrap gap-2 pr-3">
            {activeSeries.length === 0 
              ? <span className="text-sm font-semibold" style={{ color: themeColors.text }}>Select Weeks</span> 
              : activeSeries.map((s) => (
                  <span 
                    key={s.id} 
                    className="px-2.5 py-0.5 text-[12px] font-bold rounded-full whitespace-nowrap border"
                    style={{ 
                      color: s.color,
                      backgroundColor: `${s.color}15`, // 15% opacity tint of the series color
                      borderColor: `${s.color}30`      // 30% opacity border
                    }}
                  >
                    {s.primaryLabel}
                  </span>
                ))}
          </div>
          <ChevronDown 
            className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} 
            style={{ color: themeColors.textSecondary }} 
          />
        </div>

        {isOpen && (
          <div className="absolute top-full right-0 mt-2 w-[340px] p-4 rounded-xl z-50 bg-[#1e293b] border border-slate-700 shadow-2xl">
            {renderDropdownSection("Recent weeks", processedSeries.recent)}
            {renderDropdownSection("Baselines", processedSeries.baselines)}
            {renderDropdownSection("Year over year", processedSeries.yoy)}
            {renderDropdownSection("Custom weeks", processedSeries.custom)}

            <div className="mt-3 pt-3 border-t border-slate-700">
              {/* {!isAddingCustomWeek ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsAddingCustomWeek(true); }}
                  className="flex items-center gap-2 px-2 py-2 w-full rounded-md hover:bg-slate-700/50 text-blue-400 font-semibold text-sm transition-colors"
                >
                  <Plus size={16} /> Add a week
                </button>
              ) : (
                <div className="flex items-center gap-2 px-1" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="date"
                    value={customDate}
                    max={maxAllowedDate} 
                    onChange={(e) => setCustomDate(e.target.value)}
                    style={{ colorScheme: "dark" }}
                    className="flex-1 bg-[#0f172a] border border-slate-600 rounded px-2 py-1.5 text-sm outline-none text-white [&::-webkit-calendar-picker-indicator]:invert"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (customDate && onAddCustomWeek) onAddCustomWeek(customDate);
                      setIsAddingCustomWeek(false);
                      setCustomDate("");
                    }}
                    className="px-3 py-1.5 rounded text-sm font-bold bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setIsAddingCustomWeek(false); setCustomDate(""); }}
                    className="px-3 py-1.5 rounded text-sm font-medium text-slate-400 hover:text-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              )} */}
              {(() => {
                const isLight = theme === "white" || theme === "light";
                return (
                  <div className="flex items-center gap-2 px-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="date"
                      value={customDate}
                      max={maxAllowedDate} 
                      onChange={(e) => setCustomDate(e.target.value)}
                      // We use ONLY the native colorScheme here. No CSS inversion needed.
                      style={{ 
                        colorScheme: isLight ? "light" : "dark",
                        backgroundColor: themeColors.background,
                        borderColor: themeColors.border,
                        color: themeColors.text
                      }}
                      className="flex-1 border rounded px-2 py-1.5 text-sm outline-none transition-colors"
                    />
                    
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (customDate && onAddCustomWeek) onAddCustomWeek(customDate);
                        setIsAddingCustomWeek(false);
                        setCustomDate("");
                      }}
                      className="px-3 py-1.5 rounded text-sm font-bold transition-colors"
                      style={{
                        backgroundColor: themeColors.primary || "#3b82f6",
                        color: "#ffffff"
                      }}
                    >
                      Add
                    </button>
                    
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setIsAddingCustomWeek(false); setCustomDate(""); }}
                      className="px-3 py-1.5 rounded text-sm font-medium transition-colors"
                      style={{ color: themeColors.textSecondary }}
                    >
                      Cancel
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      <ReactApexChart
        key={`weekly-${isWheelZoomActive}-${activeSeries.map((s) => s.name).join("-")}`}
        options={options}
        series={activeSeries}
        type="line"
        height={height}
      />

      <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
        {activeSeries.map((series) => (
          <div
            key={series.name}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-[#0f172a] cursor-pointer hover:bg-slate-800 transition-colors"
            onClick={() => toggleSeries(series.name)}
          >
            {series.isLineIcon ? (
              <div 
                style={{ 
                  width: '14px', 
                  height: '3px', 
                  backgroundColor: series.color, 
                  borderStyle: series.dashArray > 0 ? 'dashed' : 'solid', 
                  borderWidth: '0', 
                  borderTopWidth: '2px', 
                  borderColor: series.color 
                }} 
              />
            ) : (
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: series.color }} />
            )}
            <span className="text-[12px] font-bold text-slate-200">{series.primaryLabel}</span>
            {series.secondaryLabel && (
              <span className="text-[12px] text-slate-400">{series.secondaryLabel}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}